import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HandCoins, Landmark, Wallet } from 'lucide-react';
import Loader from '../../../shared/components/ui/Loader';
import {
  EmptyRow,
  Field,
  FilterBar,
  SegmentedControl,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../../shared/components/ui/primitives';
import { getSiteWalletPayments, getSiteWallets } from '../../services/api/implementation/impl';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { HolderName, money, shortDate } from './siteWalletUi';

const TYPES = [
  { value: '', label: 'All' },
  { value: 'MONEY_ADDED', label: 'Money added' },
  { value: 'REIMBURSEMENT', label: 'Reimbursements' },
  { value: 'SETTLEMENT_PAYOUT', label: 'Settlement payouts' }
];

const KIND_LABEL = {
  MONEY_ADDED: 'Money added',
  REIMBURSEMENT: 'Reimbursement',
  SETTLEMENT_PAYOUT: 'Settlement payout'
};

const MODE_LABEL = { CASH: 'Cash', UPI: 'UPI', BANK: 'Bank' };

const filterInput =
  'rounded-control border border-hairline-strong bg-surface-muted px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-brand-700';

const EMPTY = { type: '', user_id: '', project_id: '', from: '', to: '' };

/**
 * Every payment that went out of the office for site wallets, in one place:
 * money added to wallets, own-pocket reimbursements paid back, and payouts
 * after settlement. Money added belongs to a person's wallet, not a project,
 * so the project filter narrows the list to reimbursements.
 */
const PaymentsPanel = () => {
  const [filters, setFilters] = useState(EMPTY);
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const { projects = [] } = useProjectsList();

  const payments = useQuery({
    queryKey: ['siteWalletPayments', filters],
    queryFn: () => getSiteWalletPayments(filters)
  });

  // Everyone who has ever held a wallet, closed ones included.
  const wallets = useQuery({
    queryKey: ['siteWallets', { status: 'ACTIVE,FROZEN,CLOSED' }],
    queryFn: () => getSiteWallets({ status: 'ACTIVE,FROZEN,CLOSED' })
  });
  const people = useMemo(() => {
    const byId = new Map();
    (wallets.data ?? []).forEach((w) => w.user && byId.set(w.user.id, w.user));
    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [wallets.data]);

  const rows = payments.data?.rows ?? [];
  const totals = payments.data?.totals ?? {};
  const filtered = Object.entries(filters).some(([, v]) => v);

  return (
    <>
      <TileRow>
        <Tile
          variant="card"
          label="Money added"
          hint="Paid into wallets"
          value={money(totals.money_added ?? 0)}
          icon={<Wallet size={16} />}
        />
        <Tile
          variant="card"
          label="Reimbursements paid"
          hint="Own-pocket spend paid back"
          value={money(totals.reimbursements ?? 0)}
          icon={<HandCoins size={16} />}
        />
        <Tile
          variant="card"
          label="Settlement payouts"
          hint="Owed at settlement, paid"
          value={money(totals.settlement_payouts ?? 0)}
          icon={<Landmark size={16} />}
        />
        <Tile
          variant="card"
          label="Total paid out"
          hint={filtered ? 'For these filters' : 'All time'}
          value={money(totals.total ?? 0)}
          tone="success"
        />
      </TileRow>

      <FilterBar
        end={
          <>
            <Field label="From" size="caption">
              <input
                type="date"
                value={filters.from}
                onChange={set('from')}
                className={filterInput}
              />
            </Field>
            <Field label="To" size="caption">
              <input type="date" value={filters.to} onChange={set('to')} className={filterInput} />
            </Field>
            {filtered && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY)}
                className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
              >
                Clear
              </button>
            )}
          </>
        }
      >
        <SegmentedControl
          ariaLabel="Payment type"
          size="sm"
          value={filters.type}
          onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
          options={TYPES}
        />
        <Field label="Site engineer" size="caption">
          <select value={filters.user_id} onChange={set('user_id')} className={filterInput}>
            <option value="">Everyone</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Project" size="caption">
          <select value={filters.project_id} onChange={set('project_id')} className={filterInput}>
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      </FilterBar>

      {filters.project_id && (
        <p className="text-sm text-gray-500">
          Showing reimbursements only: money added goes to a person&apos;s wallet, not to a project.
        </p>
      )}

      {payments.isLoading ? (
        <Loader />
      ) : (
        <TableCard>
          <Table>
            <THead>
              <tr>
                <Th>Paid on</Th>
                <Th>Type</Th>
                <Th>Site engineer</Th>
                <Th>Project</Th>
                <Th>Mode</Th>
                <Th>Reference / transaction ID</Th>
                <Th>Paid by</Th>
                <Th>Note</Th>
                <Th align="right">Amount</Th>
              </tr>
            </THead>
            <TBody>
              {!rows.length && (
                <EmptyRow colSpan={9}>
                  No payments{filtered ? ' for these filters' : ' yet'}.
                </EmptyRow>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <Td className="whitespace-nowrap text-gray-600">{shortDate(r.date)}</Td>
                  <Td>{KIND_LABEL[r.kind] ?? r.kind}</Td>
                  <Td>
                    <HolderName user={r.holder} />
                  </Td>
                  <Td className="text-gray-600">
                    {r.project_name ?? '—'}
                    {r.category_name && (
                      <div className="text-sm text-gray-500">{r.category_name}</div>
                    )}
                  </Td>
                  <Td className="text-gray-600">{MODE_LABEL[r.mode] ?? r.mode ?? '—'}</Td>
                  <Td className="text-gray-600">{r.reference || '—'}</Td>
                  <Td className="text-gray-600">{r.paid_by_name ?? '—'}</Td>
                  <Td className="max-w-xs text-sm text-gray-600">{r.note || '—'}</Td>
                  <Td align="right" numeric className="font-medium">
                    {money(r.amount)}
                  </Td>
                </tr>
              ))}
            </TBody>
          </Table>
        </TableCard>
      )}
      {payments.data?.truncated && (
        <p className="text-sm text-gray-500">
          Showing the latest 1,000 payments. Narrow the dates to see older ones.
        </p>
      )}
    </>
  );
};

export default PaymentsPanel;
